/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================
*/

import { useEffect, useMemo, useState } from "react";

// @mui
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Tabla del template
import Table from "examples/Tables/Table";

// axios con token
import api from "services/api";

/* ------- mini helper para mostrar portada protegida ------- */
function AttachmentThumb({ name, size = 32, radius = 8, alt = "" }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let canceled = false;
    let objectUrl = null;

    (async () => {
      if (!name) {
        setSrc(null);
        return;
      }
      const noExt = String(name).replace(/\.[^.]+$/, "");

      const candidates = [
        `/attachments/${encodeURIComponent(name)}`,
        `/attachments/${encodeURIComponent(noExt)}`,
      ];

      for (const url of candidates) {
        try {
          const res = await api.get(url, { responseType: "blob" });
          if (res?.status === 200 && res?.data) {
            objectUrl = URL.createObjectURL(res.data);
            if (!canceled) setSrc(objectUrl);
            break;
          }
        } catch {
          /* probar siguiente */
        }
      }
    })();

    return () => {
      canceled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [name]);

  if (!src) return null;

  return (
    <img
      src={src}
      alt={alt || name}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        objectFit: "cover",
        display: "block",
        background: "#eee",
      }}
    />
  );
}

/* ===================== COMPONENTE ===================== */
/**
 * props esperadas:
 *   latest: [{ id, artist, title, status, created_at, cover }]
 *   loading: boolean
 */
export default function Projects({ latest = [], loading = false, onItemClick }) {
  const [menu, setMenu] = useState(null);
  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = () => setMenu(null);

  const statusChip = (s) => {
    if (s === 1) return <Chip size="small" color="success" label="ACEPTADO" variant="outlined" />;
    if (s === 2) return <Chip size="small" color="error" label="RECHAZADO" variant="outlined" />;
    return <Chip size="small" color="warning" label="PENDIENTE" variant="outlined" />;
  };

  const { columns, rows } = useMemo(() => {
    const cols = [
      { name: "artista", align: "left" }, // 👈 renombrada
      { name: "titulo", align: "left" },
      { name: "estatus", align: "center" },
    ];

    const rs = (latest.length ? latest : Array(6).fill({})).map((it, i) => {
      const avatar =
        it?.cover ? (
          <AttachmentThumb name={it.cover} alt={it.artist} />
        ) : (
          <SoftBox
            width="32px"
            height="32px"
            borderRadius="50%"
            display="grid"
            placeItems="center"
            sx={{ background: "linear-gradient(195deg, #49a3f1, #1A73E8)" }}
          >
            <SoftTypography variant="button" color="white">
              {(it.artist || "?").slice(0, 1).toUpperCase()}
            </SoftTypography>
          </SoftBox>
        );
      const handleClick = () => {
        if (!onItemClick || loading || !it) return;
        onItemClick(it);
      };

      return {
        artista: (
          <SoftBox
          display="flex"
          alignItems="center"
          gap={1.2}
          onClick={handleClick}
          sx={{
            cursor: onItemClick && !loading ? "pointer" : "default",
          }}
        >
            {avatar}
            <SoftTypography variant="button" color="text">
              {loading ? "…" : it.artist || "—"}
            </SoftTypography>
          </SoftBox>
        ),
        titulo: (
          <SoftTypography
            variant="button"
            color="text"
            onClick={handleClick}
            sx={{
              cursor: onItemClick && !loading ? "pointer" : "default",
              textDecoration: onItemClick && !loading ? "underline" : "none",
            }}
          >
            {loading ? "…" : it.title || "—"}
          </SoftTypography>
        ),
        estatus: loading ? "…" : statusChip(it.status),
      };
    });

    return { columns: cols, rows: rs };
  }, [latest, loading, onItemClick]);

  // contador “este mes”
  const monthCount = useMemo(() => {
    if (!latest.length) return 0;
    const now = new Date();
    return latest.filter((x) => {
      const d = new Date(x.created_at || Date.now());
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [latest]);

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(menu)}
      onClose={closeMenu}
    >
      <MenuItem onClick={closeMenu}>Refrescar</MenuItem>
      <MenuItem onClick={closeMenu}>Exportar CSV</MenuItem>
    </Menu>
  );

  return (
    <Card>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <SoftBox>
          <SoftTypography variant="h6" gutterBottom>
            Últimas certificaciones
          </SoftTypography>
          <SoftBox display="flex" alignItems="center" lineHeight={0}>
            <Icon
              sx={{ fontWeight: "bold", color: ({ palette: { info } }) => info.main, mt: -0.5 }}
            >
              done
            </Icon>
            <SoftTypography variant="button" fontWeight="regular" color="text">
              &nbsp;<strong>{monthCount}</strong> este mes
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        {renderMenu}
      </SoftBox>

      <SoftBox
        sx={{
          "& .MuiTableRow-root:not(:last-child)": {
            "& td": ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          },
        }}
      >
        <Table columns={columns} rows={rows} />
      </SoftBox>
    </Card>
  );
}
